import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRegistrationMode } from '../api/queries.js';
import { useReveal } from '../lib/useReveal.js';
import { applyTheme } from '../theme.js';
import { useIsDark } from '../lib/useIsDark.js';
import LandingLogo from '../components/landing/LandingLogo.js';
import PhoneFrame from '../components/landing/PhoneFrame.js';
import Shot from '../components/landing/Shot.js';
import HeroScroller from '../components/landing/HeroScroller.js';
import { LANGUAGE_NAMES, UI_LANGUAGES, type RegistrationMode, type UiLanguage } from '@b4b/shared';

// Landing for signed-out visitors (spec docs/superpowers/specs/2026-09-08-landing-page-design.md,
// visual design docs/design.pen). Static copy via i18n, the only API call is registration-mode.

const LAYOUTS = ['reels', 'list', 'grid', 'topics'] as const;
// staggered phone row on desktop (design: 40 / 0 / 56 / 16 px top offsets)
const LAYOUT_OFFSET: Record<(typeof LAYOUTS)[number], string> = {
  reels: 'lg:pt-10',
  list: '',
  grid: 'lg:pt-14',
  topics: 'lg:pt-4',
};

function XIcon({ className = 'h-[11px] w-[11px]' }: { className?: string }) {
  return (
    <svg viewBox="0 0 12 12" aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M2 2l8 8M10 2l-8 8" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="m8 12 3 3 5-6" />
    </svg>
  );
}

function ThemeIcon({ dark }: { dark: boolean }) {
  return dark ? (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

function CtaRow({ mode, className = '' }: { mode: RegistrationMode | undefined; className?: string }) {
  const { t } = useTranslation();
  const canRegister = mode !== 'closed';
  return (
    <div className={`flex w-full flex-col gap-2.5 sm:w-auto sm:flex-row sm:gap-3 ${className}`}>
      {canRegister && (
        <Link
          to="/login?mode=register"
          data-cta="register"
          className="inline-flex h-12 items-center justify-center rounded-xl bg-accent px-6 font-semibold text-white hover:bg-accent-strong"
        >
          {t('landing.hero.ctaPrimary')}
        </Link>
      )}
      <Link
        to="/login"
        data-cta="login"
        className={`inline-flex h-12 items-center justify-center rounded-xl px-6 font-semibold ${
          canRegister
            ? 'border border-neutral-200 text-neutral-900 hover:bg-neutral-100 dark:border-white/10 dark:text-slate-100 dark:hover:bg-white/5'
            : 'bg-accent text-white hover:bg-accent-strong'
        }`}
      >
        {t('landing.hero.ctaSecondary')}
      </Link>
    </div>
  );
}

function inviteNoteKey(mode: RegistrationMode | undefined): string {
  if (mode === 'open') return 'landing.hero.openNote';
  if (mode === 'closed') return 'landing.hero.closedNote';
  return 'landing.hero.inviteNote';
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const dark = useIsDark();
  const registration = useRegistrationMode();
  const mode = registration.data?.mode;
  useReveal();

  const lang: UiLanguage = (UI_LANGUAGES as readonly string[]).includes(i18n.resolvedLanguage ?? '')
    ? (i18n.resolvedLanguage as UiLanguage)
    : 'en';
  useEffect(() => {
    document.querySelector('meta[name="description"]')?.setAttribute('content', t('landing.meta.description'));
  }, [lang, t]);

  // a missing/misspelled key yields a string, not an array — never let the public entry point crash on it
  const tArray = <T,>(key: string): T[] => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? (value as T[]) : [];
  };
  const whyItems = tArray<{ title: string; body: string }>('landing.why.items');
  const muted = tArray<string>('landing.hero.muted');
  const bullets = tArray<string>('landing.detail.bullets');
  const chips = tArray<string>('landing.sources.chips');
  const muteTags = tArray<string>('landing.sources.muteTags');

  const h2 = 'text-[28px] font-semibold leading-[1.2] tracking-tight sm:text-4xl sm:leading-[1.22]';
  const secondary = 'text-neutral-500 dark:text-slate-400';
  const container = 'mx-auto w-full max-w-[1120px] px-5 sm:px-12';

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 dark:bg-slate-900 dark:text-slate-100" data-page="landing">
      {/* 00 top bar */}
      <header className={`${container} flex h-14 items-center justify-between sm:h-16`}>
        <LandingLogo />
        <Link to="/login" className={`flex min-h-[44px] items-center text-sm font-medium ${secondary} hover:text-accent`}>
          {t('landing.nav.login')}
        </Link>
      </header>

      <main>
        {/* 01 hero */}
        <section className={`${container} flex flex-col gap-8 pb-12 pt-8 md:flex-row md:items-center md:gap-9 md:pt-12 lg:gap-10 lg:pb-12 lg:pt-16`} data-section="hero">
          <div className="flex flex-col gap-5 md:flex-1 lg:w-[560px] lg:flex-none lg:gap-6">
            <div className="flex flex-col gap-1">
              <h1 className="text-[72px] font-bold leading-[1.05] tracking-[-0.02em] md:text-[84px] lg:text-[104px]">
                b<span className="text-accent">4</span>b
              </h1>
              <p className={`text-lg font-medium md:text-xl lg:text-2xl ${secondary}`}>{t('landing.hero.expansion')}</p>
            </div>
            <p className={`max-w-[470px] text-[17px] leading-[1.55] sm:text-lg sm:leading-[1.56] ${secondary}`}>
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 text-sm sm:text-[15px]">
              <p className="font-medium">{t('landing.hero.mutedLabel')}</p>
              <ul className="contents">
                {muted.map((word) => (
                  <li key={word} className={`flex items-center gap-1.5 ${secondary}`}>
                    <span>{word}</span>
                    <XIcon />
                  </li>
                ))}
              </ul>
            </div>
            <CtaRow mode={mode} className="pt-1" />
            <p className={`max-w-[440px] text-sm leading-[1.43] ${secondary}`}>{t(inviteNoteKey(mode))}</p>
          </div>
          <div className="flex justify-center pt-6 md:pt-0 lg:flex-1">
            <PhoneFrame className="h-[560px] w-[280px] md:h-[600px] md:w-[300px] lg:h-[780px] lg:w-[390px]">
              <HeroScroller />
            </PhoneFrame>
          </div>
        </section>

        {/* 02 why */}
        <section className={`${container} py-8 lg:pt-16 lg:pb-12`} data-section="why" data-reveal>
          <div className="flex flex-col gap-2 md:flex-row md:gap-12 lg:gap-20">
            <div className="flex flex-col gap-2 md:w-[240px] md:shrink-0 lg:w-[360px] lg:gap-3">
              <h2 className={h2}>{t('landing.why.title')}</h2>
              <p className={`text-[15px] leading-[1.5] sm:text-base ${secondary}`}>{t('landing.why.subtitle')}</p>
            </div>
            <ol className="mt-3 flex flex-1 flex-col divide-y divide-neutral-200 dark:divide-white/10 md:mt-0">
              {whyItems.map((item, i) => (
                <li key={item.title} className="flex flex-col gap-1.5 py-[18px] lg:flex-row lg:gap-8 lg:py-6">
                  {/* mobile/tablet: "01  Title" on one row; desktop: three columns (display: contents) */}
                  <div className="flex items-center gap-3 lg:contents">
                    <span className="text-[15px] font-semibold tracking-[0.07em] text-accent">{String(i + 1).padStart(2, '0')}</span>
                    <h3 className="text-[19px] font-semibold leading-[1.3] sm:text-[22px] lg:w-[230px] lg:shrink-0">{item.title}</h3>
                  </div>
                  <p className={`text-base leading-[1.55] ${secondary}`}>{item.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 03 layouts */}
        <section className="py-8 lg:py-12" data-section="layouts" data-reveal>
          <div className={`${container} flex flex-col gap-1.5 lg:flex-row lg:items-end lg:justify-between`}>
            <h2 className={h2}>{t('landing.layouts.title')}</h2>
            <p className={`text-[15px] leading-[1.5] sm:text-base ${secondary}`}>{t('landing.layouts.subtitle')}</p>
          </div>
          <ul className="mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:px-12 lg:mx-auto lg:mt-10 lg:max-w-[1120px] lg:justify-between lg:overflow-visible lg:px-12">
            {LAYOUTS.map((layout) => (
              <li key={layout} className={`flex w-[220px] shrink-0 snap-center flex-col items-center gap-4 lg:w-[250px] ${LAYOUT_OFFSET[layout]}`}>
                <PhoneFrame size="sm" className="h-[440px] w-[220px]">
                  <Shot
                    name={`layout-${layout}`}
                    alt={t('landing.layouts.shotAlt', { name: t(`landing.layouts.items.${layout}.title`) })}
                    width={780}
                    height={1560}
                  />
                </PhoneFrame>
                <div className="flex flex-col items-center gap-1 text-center">
                  <h3 className="font-semibold">{t(`landing.layouts.items.${layout}.title`)}</h3>
                  <p className={`text-sm leading-[1.43] ${secondary}`}>{t(`landing.layouts.items.${layout}.desc`)}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* 04 article detail */}
        <section className={`${container} flex flex-col gap-6 py-8 md:flex-row md:items-center md:gap-9 lg:gap-14 lg:py-12`} data-section="detail" data-reveal>
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-100 shadow-sm dark:border-white/10 dark:bg-slate-800 md:w-[410px] md:shrink-0 lg:w-[660px]">
            <Shot name="detail" alt={t('landing.detail.shotAlt')} width={1320} height={1040} className="aspect-[660/520]" />
          </div>
          <div className="flex flex-col gap-3 lg:gap-5">
            <h2 className={h2}>{t('landing.detail.title')}</h2>
            <p className={`text-base leading-[1.55] sm:text-lg sm:leading-[1.56] ${secondary}`}>{t('landing.detail.body')}</p>
            <ul className="flex flex-col gap-2.5 pt-1.5 lg:gap-3.5 lg:pt-2">
              {bullets.map((label) => (
                <li key={label} className="flex items-center gap-3 font-medium">
                  <CheckCircleIcon className="h-5 w-5 shrink-0 text-accent" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 05 sources + mute demo */}
        <section className={`${container} flex flex-col gap-4 py-8 md:flex-row md:items-center md:gap-10 lg:gap-20 lg:py-12`} data-section="sources" data-reveal>
          <div className="flex flex-col gap-3 md:flex-1 lg:w-[600px] lg:flex-none lg:gap-5">
            <h2 className={h2}>{t('landing.sources.title')}</h2>
            <p className={`text-base leading-[1.55] sm:text-lg sm:leading-[1.56] ${secondary}`}>{t('landing.sources.body')}</p>
            <ul className="flex flex-wrap gap-2 pt-1 lg:gap-2.5 lg:pt-2">
              {[...chips, t('landing.sources.yourSite')].map((chip) => (
                <li key={chip} className="rounded-full border border-neutral-200 px-3.5 py-2 text-sm dark:border-white/10">
                  {chip}
                </li>
              ))}
            </ul>
          </div>
          <div className="mx-auto flex w-full max-w-[440px] flex-col items-center gap-3 pt-4 md:w-[320px] md:shrink-0 md:pt-0 lg:w-full">
            <div aria-hidden="true" className="contents">
            <div className="rounded-[10px] border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium shadow-sm dark:border-white/10 dark:bg-slate-800">
              {t('landing.sources.muteTooltip')}
            </div>
            <div className="flex w-full flex-col gap-3.5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-800">
              <p className="text-[17px] font-semibold leading-[1.4]">{t('landing.sources.muteCardTitle')}</p>
              <div className="flex flex-wrap gap-2">
                {muteTags.map((tag) => (
                  <span key={tag} className={`flex items-center gap-1.5 rounded-full border border-neutral-200 px-3.5 py-2 text-sm dark:border-white/10 ${secondary}`}>
                    <span className="text-neutral-900 dark:text-slate-100">{tag}</span>
                    <XIcon className="h-3 w-3" />
                  </span>
                ))}
              </div>
            </div>
            </div>
            <p className={`text-center text-sm leading-[1.43] ${secondary}`}>{t('landing.sources.muteCaption')}</p>
          </div>
        </section>

        {/* 06 the point */}
        <section className={`${container} flex flex-col items-center gap-[18px] py-20 text-center lg:gap-6 lg:py-40`} data-section="done" data-reveal>
          <p className={`text-base sm:text-lg ${secondary}`}>{t('landing.done.scrollMore')}</p>
          <CheckCircleIcon className="h-11 w-11 text-green-500 sm:h-12 sm:w-12" />
          <p className={`${h2} flex flex-col gap-0.5 lg:gap-1`}>
            <span>{t('landing.done.line1')}</span>
            <span>{t('landing.done.line2')}</span>
          </p>
          <p className={`text-[15px] leading-[1.45] sm:text-base sm:leading-[1.5] ${secondary}`}>{t('landing.done.sub')}</p>
        </section>

        {/* 07 CTA */}
        <section className={`${container} flex flex-col items-center gap-3 pb-16 pt-4 text-center lg:gap-5 lg:pb-24 lg:pt-12`} data-section="cta" data-reveal>
          <CtaRow mode={mode} />
          <p className={`text-sm leading-[1.43] ${secondary}`}>{t(inviteNoteKey(mode))}</p>
        </section>
      </main>

      {/* 08 footer */}
      <footer className="border-t border-neutral-200 dark:border-white/10">
        <div className={`${container} flex flex-col items-center gap-2.5 py-7 text-center text-sm lg:h-20 lg:flex-row lg:justify-between lg:py-0 lg:text-left ${secondary}`}>
          <span className="font-medium lg:font-normal">{t('landing.footer.brand')}</span>
          <span className="text-[13px] leading-[1.5] sm:text-sm">{t('landing.footer.claims')}</span>
          <div className="flex items-center gap-3.5 pt-1.5 lg:pt-0">
            <select
              value={lang}
              onChange={(e) => void i18n.changeLanguage(e.target.value as UiLanguage)}
              aria-label={t('landing.footer.language')}
              className="min-h-[44px] rounded-lg border border-neutral-200 bg-white px-2 text-sm font-medium dark:border-white/10 dark:bg-neutral-950"
            >
              {UI_LANGUAGES.map((code) => (
                <option key={code} value={code}>
                  {LANGUAGE_NAMES[code]}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => applyTheme(dark ? 'light' : 'dark')}
              aria-label={t('landing.footer.theme')}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center hover:text-accent"
            >
              <ThemeIcon dark={dark} />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
